import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Wallet, ArrowLeft, ArrowDownToLine, Loader2, Settings, History } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  getMyWallet,
  getMyWalletTransactions,
  getMyPayoutRequests,
  requestPayout,
  getMyPayoutProfile,
} from "@/lib/wallet.functions";

export const Route = createFileRoute("/wallet")({
  head: () => ({ meta: [{ title: "Mi Wallet — ZYNTRA" }] }),
  component: WalletPage,
});

function fmt(n: number, currency = "CRC") {
  return `${currency} ${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 0 })}`;
}

async function withAuth<T>(fn: (h: any) => Promise<T>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return fn({ authorization: token ? `Bearer ${token}` : "" });
}

function WalletPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [wallet, setWallet] = useState<any>(null);
  const [txs, setTxs] = useState<any[]>([]);
  const [requests, setRequests] = useState<any[]>([]);
  const [hasProfile, setHasProfile] = useState(false);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      await withAuth(async (headers) => {
        const [w, t, r, p] = await Promise.all([
          getMyWallet({ headers } as any),
          getMyWalletTransactions({ headers } as any),
          getMyPayoutRequests({ headers } as any),
          getMyPayoutProfile({ headers } as any),
        ]);
        setWallet(w.wallet);
        setTxs(t.transactions);
        setRequests(r.requests);
        setHasProfile(!!p.profile);
      });
    } catch (e: any) {
      setMsg({ type: "err", text: e.message });
    }
    setLoading(false);
  }

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    load();
  }, [user, authLoading]);

  async function handleRequest() {
    setMsg(null);
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setMsg({ type: "err", text: "Ingresá un monto válido." }); return;
    }
    if (!hasProfile) {
      navigate({ to: "/settings/payouts" }); return;
    }
    setSubmitting(true);
    try {
      await withAuth((headers) => requestPayout({ data: { amount: amt }, headers } as any));
      setMsg({ type: "ok", text: "Solicitud enviada." });
      setAmount("");
      load();
    } catch (e: any) {
      setMsg({ type: "err", text: e.message });
    }
    setSubmitting(false);
  }

  if (loading || !wallet) {
    return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const currency = wallet.currency || "CRC";

  return (
    <div className="mx-auto max-w-3xl px-4 pb-20 pt-24 sm:px-6">
      <Link to="/" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Volver
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <Wallet className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-black text-foreground sm:text-3xl">Mi Wallet</h1>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-6">
        <div className="rounded-2xl border border-border bg-card p-5 sm:col-span-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Disponible</p>
          <p className="mt-2 text-3xl font-black gradient-text">{fmt(wallet.available_balance, currency)}</p>
          <p className="mt-1 text-xs text-muted-foreground">Listo para retirar</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total ganado</p>
          <p className="mt-2 text-xl font-bold text-foreground">{fmt(wallet.total_earned, currency)}</p>
        </div>
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total retirado</p>
          <p className="mt-2 text-xl font-bold text-foreground">{fmt(wallet.total_paid_out, currency)}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground flex items-center gap-2">
            <ArrowDownToLine className="h-5 w-5 text-primary" />
            Solicitar retiro
          </h2>
          <Link to="/settings/payouts" className="text-xs text-primary hover:underline flex items-center gap-1">
            <Settings className="h-3 w-3" />
            {hasProfile ? "Editar método de pago" : "Configurar método"}
          </Link>
        </div>
        {!hasProfile && (
          <p className="mb-3 rounded-lg bg-warning/10 px-3 py-2 text-xs text-warning">
            Necesitás configurar tu método de pago antes de solicitar un retiro.
          </p>
        )}
        <div className="flex gap-2">
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder={`Monto (máx. ${fmt(wallet.available_balance, currency)})`}
            min={0}
            max={wallet.available_balance}
            className="flex-1 rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
          />
          <button
            onClick={handleRequest}
            disabled={submitting || Number(wallet.available_balance) <= 0}
            className="rounded-xl gradient-primary px-5 py-2 text-sm font-bold text-primary-foreground disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Solicitar"}
          </button>
        </div>
        {msg && (
          <p className={`mt-3 rounded-lg px-3 py-2 text-xs ${msg.type === "ok" ? "bg-success/10 text-success" : "bg-destructive/10 text-destructive"}`}>
            {msg.text}
          </p>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 mb-6">
        <h2 className="text-lg font-bold text-foreground mb-4">Solicitudes de retiro</h2>
        {requests.length === 0 ? (
          <p className="text-sm text-muted-foreground">Aún no solicitaste retiros.</p>
        ) : (
          <div className="space-y-2">
            {requests.map((r) => (
              <div key={r.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm">
                <div>
                  <p className="font-bold text-foreground">{fmt(r.amount, r.currency)}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.requested_at).toLocaleString("es-CR")}</p>
                  {r.admin_notes && <p className="mt-1 text-xs text-muted-foreground">Nota: {r.admin_notes}</p>}
                </div>
                <PayoutStatusBadge status={r.status} />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <History className="h-5 w-5 text-primary" />
          Movimientos
        </h2>
        {txs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Sin movimientos.</p>
        ) : (
          <div className="space-y-2">
            {txs.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2 text-sm">
                <div>
                  <p className="font-medium text-foreground capitalize">{tx.type.replace(/_/g, " ")}</p>
                  <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString("es-CR")}</p>
                  {tx.description && <p className="text-xs text-muted-foreground">{tx.description}</p>}
                </div>
                <p className={`font-bold ${Number(tx.amount) >= 0 ? "text-success" : "text-destructive"}`}>
                  {Number(tx.amount) >= 0 ? "+" : ""}{fmt(tx.amount, tx.currency)}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PayoutStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "bg-warning/20 text-warning",
    approved: "bg-primary/20 text-primary",
    paid: "bg-success/20 text-success",
    rejected: "bg-destructive/20 text-destructive",
    cancelled: "bg-muted text-muted-foreground",
  };
  const labels: Record<string, string> = {
    pending: "Pendiente",
    approved: "Aprobado",
    paid: "Pagado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
}
